import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Users } from "lucide-react";
import { ProductPreview } from "./ProductPreview";
import { useQueueCount } from "@/hooks/useQueueCount";
import { NotifyMeButton } from "@/components/NotifyMeButton";

const lineParent = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.15 },
  },
};

const lineChild = {
  hidden: { y: "110%" },
  show: {
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

const fadeUp = {
  hidden: { y: 24, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.7, ease: "easeOut" } },
};

const MaskedLine = ({ children }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span variants={lineChild} className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const navigate = useNavigate();
  const repsAvailable = useQueueCount();
  return (
    <section className="relative overflow-hidden px-6 pt-36 pb-20 lg:pt-44 lg:pb-28">
      {/* ambient background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-0 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(229,37,37,0.12),transparent_60%)] blur-3xl"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 lg:grid-cols-12 lg:gap-10">
        {/* LEFT */}
        <div className="lg:col-span-5">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#E52525]/30 bg-[#E52525]/10 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FF5A5A]"
          >
            <span className="sp-live-dot h-1.5 w-1.5 rounded-full bg-[#E52525]" />
            Live Sales Practice
          </motion.span>

          <motion.h1
            variants={lineParent}
            initial="hidden"
            animate="show"
            className="font-heading mt-7 text-[3.4rem] font-black leading-[0.92] tracking-tighter text-white sm:text-6xl lg:text-7xl"
          >
            <MaskedLine>STEP INTO</MaskedLine>
            <MaskedLine>
              THE <span className="text-[#E52525]">PIT.</span>
            </MaskedLine>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.55 }}
            className="mt-7 max-w-lg text-xl font-light leading-relaxed text-zinc-300"
          >
            Instantly match with another salesperson for a live mock call.
            Real conversations, real pressure — sharper with every rep.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.7 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row"
          >
            <button
              data-testid="enter-the-pit-btn"
              onClick={() => navigate("/login")}
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#E52525] px-8 py-4 text-base font-semibold text-white shadow-[0_0_30px_rgba(229,37,37,0.35)] transition-[background-color,transform,box-shadow] duration-300 hover:bg-[#FF3333] hover:shadow-[0_0_45px_rgba(229,37,37,0.5)] active:scale-95"
            >
              Enter The Pit
              <ArrowRight
                size={19}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
            <button
              data-testid="practice-with-friend-btn"
              onClick={() => navigate("/login")}
              className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/20 bg-transparent px-8 py-4 text-base font-semibold text-white transition-colors duration-300 hover:bg-white/5 hover:border-white/40"
            >
              <Users size={18} className="text-zinc-400" />
              Practice With Friend
            </button>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-x-4 gap-y-3 text-sm text-zinc-500"
            data-testid="reps-available-hero"
          >
            {repsAvailable === null ? (
              <span className="text-zinc-500">Checking who's in the pit…</span>
            ) : repsAvailable === 0 ? (
              <>
                <span className="font-medium text-zinc-300">
                  No reps available right now. Be the first in the pit!
                </span>
                <NotifyMeButton
                  variant="ghost"
                  returnTo="/"
                  testid="hero-notify-me-btn"
                />
              </>
            ) : (
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-[#22C55E]" />
                <span
                  className="font-medium text-zinc-300"
                  data-testid="reps-available-count"
                >
                  {repsAvailable} {repsAvailable === 1 ? "rep" : "reps"} available
                </span>
              </span>
            )}
          </motion.div>
        </div>

        {/* RIGHT */}
        <div className="lg:col-span-7 lg:origin-top lg:scale-[0.98]">
          <ProductPreview />
        </div>
      </div>
    </section>
  );
};
